/**
 * Marketing API Service
 * Handles API calls for marketing leads and campaigns
 */

import { api } from '../lib/api';

export interface MarketingLead {
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    year: number | null;
  };
  last_service: {
    service_type: string;
    performed_at: number | null;
    days_ago: number;
  };
}

export interface LeadsSummary {
  service_type: {
    id: string;
    name: string;
  };
  leads_count: number;
  maintenance_interval_days: number | null;
}

export interface Campaign {
  id: string;
  name: string;
  service_type: { id: string; name: string } | null;
  subject?: string;
  template_content?: string;
  status: 'draft' | 'sent' | 'cancelled';
  sent_count: number;
  created_by?: { id: string; name: string };
  created_at: number;
  sent_at: number | null;
  stats?: {
    total: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
  };
}

export interface CampaignRecipient {
  id: string;
  customer: {
    id: string;
    name: string;
    email: string;
  };
  vehicle: { id: string; registration: string } | null;
  email_status: 'queued' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced';
  sent_at: number | null;
  delivered_at: number | null;
  opened_at: number | null;
  clicked_at: number | null;
  bounced_at: number | null;
  bounce_reason: string | null;
}

export interface DailyLimitStatus {
  sent_today: number;
  limit: number;
  remaining: number;
  resets_at: string;
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Get marketing leads
 */
export async function getLeads(params: {
  serviceTypeId: string;
  daysSinceService?: number;
  hasMarketingConsent?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ leads: MarketingLead[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams({
    service_type_id: params.serviceTypeId,
    days_since_service: (params.daysSinceService || 180).toString(),
    has_marketing_consent: (params.hasMarketingConsent !== false).toString(),
    limit: (params.limit || 50).toString(),
    offset: (params.offset || 0).toString(),
  });

  return api.get(`/api/marketing/leads?${searchParams}`);
}

/**
 * Get leads summary by service type
 */
export async function getLeadsSummary(): Promise<LeadsSummary[]> {
  return api.get('/api/marketing/leads/summary');
}

/**
 * List campaigns
 */
export async function getCampaigns(params?: {
  status?: 'draft' | 'sent' | 'cancelled';
  limit?: number;
  offset?: number;
}): Promise<{ campaigns: Campaign[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return api.get(`/api/marketing/campaigns${query ? `?${query}` : ''}`);
}

/**
 * Get campaign by ID
 */
export async function getCampaign(id: string): Promise<Campaign> {
  return api.get(`/api/marketing/campaigns/${id}`);
}

/**
 * Create a new campaign draft
 */
export async function createCampaign(data: {
  name: string;
  serviceTypeId?: string;
  subject: string;
  templateContent: string;
  recipientIds: Array<{ customerId: string; vehicleId?: string }>;
}): Promise<Campaign> {
  return api.post('/api/marketing/campaigns', {
    name: data.name,
    service_type_id: data.serviceTypeId,
    subject: data.subject,
    template_content: data.templateContent,
    recipient_ids: data.recipientIds.map((r) => ({
      customer_id: r.customerId,
      vehicle_id: r.vehicleId,
    })),
  });
}

/**
 * Cancel a draft campaign
 */
export async function cancelCampaign(id: string): Promise<void> {
  return api.delete(`/api/marketing/campaigns/${id}`);
}

/**
 * Send a campaign
 */
export async function sendCampaign(id: string): Promise<{
  campaign_id: string;
  emails_queued: number;
  estimated_send_time: string;
}> {
  return api.post(`/api/marketing/campaigns/${id}/send`);
}

/**
 * Get campaign recipients
 */
export async function getCampaignRecipients(
  campaignId: string,
  params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{ recipients: CampaignRecipient[]; pagination: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return api.get(`/api/marketing/campaigns/${campaignId}/recipients${query ? `?${query}` : ''}`);
}

/**
 * Get daily email limit status
 */
export async function getDailyLimit(): Promise<DailyLimitStatus> {
  return api.get('/api/marketing/daily-limit');
}
